// proposalItemsDisplayPrefs.js
//
// Local display preferences for the Proposal Items table.

import {
  readScopedPreferenceMap,
  writeScopedPreferenceMap,
} from 'views/utils/organicViews';

export const PROPOSAL_ITEMS_DISPLAY_PREFS_STORAGE_KEY = 'appdeck.proposalItems.displayPrefs.v1';

/** Column keys that can be toggled in Settings → Manage columns. */
export const PROPOSAL_ITEM_COLUMN_KEYS = Object.freeze([
  'sku',
  'itemType',
  'category',
  'status',
  'manufacturer',
  'vendor',
  'cost',
  'msrp',
  'markup',
  'margin',
  'unitPrice',
  'room',
  'color',
  'dimensions',
  'shipping',
  'shippingMarkup',
  'taxable',
  'website',
]);

export const DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS = Object.freeze({
  showCoverPhoto: true,
  showDescription: true,
  showLineNumber: true,
  columns: Object.freeze({
    sku: true,
    itemType: false,
    category: false,
    status: false,
    manufacturer: false,
    vendor: true,
    cost: true,
    msrp: false,
    markup: true,
    margin: false,
    unitPrice: true,
    room: false,
    color: false,
    dimensions: false,
    shipping: false,
    shippingMarkup: false,
    taxable: false,
    website: false,
  }),
});

/**
 * Per-column client visibility on the Proposal document
 * (`data_proposal.clientVisibleColumns`). Separate from local Manage columns.
 *
 * Defaults hide margin-sensitive / internal columns from the client.
 * Always client-facing chrome (Item, Qty, Unit, Total, #) is not in this map.
 * `unitPrice` is forced visible (client sell price).
 */
export const DEFAULT_PROPOSAL_CLIENT_VISIBLE_COLUMNS = Object.freeze({
  sku: false,
  itemType: true,
  category: true,
  status: true,
  manufacturer: false,
  vendor: false,
  cost: false,
  msrp: false,
  markup: false,
  margin: false,
  unitPrice: true,
  room: true,
  color: true,
  dimensions: true,
  shipping: true,
  shippingMarkup: false,
  taxable: false,
  website: false,
});

/** Column keys that can be shown/hidden from the client in the editor header menu. */
export const PROPOSAL_CLIENT_TOGGLEABLE_COLUMN_KEYS = Object.freeze(
  PROPOSAL_ITEM_COLUMN_KEYS.filter((key) => key !== 'unitPrice'),
);

function cleanScopeKey(baseKey) {
  return String(baseKey || '').trim() || '_';
}

/**
 * @param {unknown} value
 * @returns {object}
 */
export function normalizeProposalItemsDisplayPrefs(value) {
  const source = value && typeof value === 'object' ? value : {};
  const sourceColumns = source.columns && typeof source.columns === 'object'
    ? source.columns
    : {};
  const columns = {};
  PROPOSAL_ITEM_COLUMN_KEYS.forEach((key) => {
    const fallback = DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS.columns[key];
    columns[key] = sourceColumns[key] !== undefined
      ? Boolean(sourceColumns[key])
      : fallback !== false;
  });
  // Unit price + total always shown in the table chrome; force unitPrice on.
  columns.unitPrice = true;

  return {
    showCoverPhoto: source.showCoverPhoto !== false,
    showDescription: source.showDescription !== false,
    showLineNumber: source.showLineNumber !== false,
    columns,
  };
}

/**
 * @param {string} [baseKey]
 * @returns {object}
 */
export function readProposalItemsDisplayPrefs(baseKey) {
  const map = readScopedPreferenceMap(PROPOSAL_ITEMS_DISPLAY_PREFS_STORAGE_KEY);
  return normalizeProposalItemsDisplayPrefs(map[cleanScopeKey(baseKey)]);
}

/**
 * @param {string} [baseKey]
 * @param {object} prefs
 * @returns {object}
 */
export function writeProposalItemsDisplayPrefs(baseKey, prefs) {
  const scope = cleanScopeKey(baseKey);
  const nextPrefs = normalizeProposalItemsDisplayPrefs(prefs);
  const map = readScopedPreferenceMap(PROPOSAL_ITEMS_DISPLAY_PREFS_STORAGE_KEY);
  writeScopedPreferenceMap(PROPOSAL_ITEMS_DISPLAY_PREFS_STORAGE_KEY, {
    ...map,
    [scope]: nextPrefs,
  });
  return nextPrefs;
}

/**
 * Normalize a client-visible columns map from the proposal document.
 *
 * @param {unknown} value
 * @returns {Record<string, boolean>}
 */
export function normalizeProposalClientVisibleColumns(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
  const columns = {};
  PROPOSAL_ITEM_COLUMN_KEYS.forEach((key) => {
    const fallback = DEFAULT_PROPOSAL_CLIENT_VISIBLE_COLUMNS[key];
    columns[key] = source[key] !== undefined
      ? Boolean(source[key])
      : fallback !== false;
  });
  // Client sell price is always visible to the client.
  columns.unitPrice = true;
  return columns;
}

/**
 * Whether a toggleable column is visible to the client.
 *
 * @param {Record<string, boolean>|null|undefined} columns
 * @param {string} columnKey
 * @returns {boolean}
 */
export function isProposalColumnClientVisible(columns, columnKey) {
  const key = String(columnKey || '').trim();
  if (!key) return true;
  if (key === 'unitPrice') return true;
  const map = columns && typeof columns === 'object'
    ? columns
    : DEFAULT_PROPOSAL_CLIENT_VISIBLE_COLUMNS;
  if (map[key] !== undefined) return Boolean(map[key]);
  return DEFAULT_PROPOSAL_CLIENT_VISIBLE_COLUMNS[key] !== false;
}
